import {
  Button,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@nexus/ui";
import {
  useEffect,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { HighlightText } from "@/features/opportunities/components/HighlightText";
import { ScriptSentToVendorCard } from "@/features/opportunities/components/scripts/ScriptSentToVendorCard/ScriptSentToVendorCard.js";
import type { SentScriptMeta } from "@/features/marketplace/types/marketplace.types";
import type {
  PersonaBriefContentPatch,
  PersonaBriefDto,
  PersonaBriefHookPackage,
} from "@/features/persona-opportunities/api/persona-opportunity-briefs.api";
import {
  PERSONA_BRIEF_AGE_RANGE_OPTIONS,
  PERSONA_BRIEF_DURATION_OPTIONS,
  PERSONA_BRIEF_GENDER_OPTIONS,
  PERSONA_BRIEF_HOOK_PACKAGE_OPTIONS,
  translatePersonaBriefClosedListValue,
  withCurrentOption,
} from "@/features/persona-opportunities/lib/persona-brief-options";

import {
  CELL_DISPLAY_SIZE_CLASS,
  PersonaScriptEditableCell,
} from "./PersonaScriptEditableCell.js";
import { PersonaScriptFieldDiff } from "./PersonaScriptFieldDiff.js";
import { usePersonaBriefClipboard } from "./usePersonaBriefClipboard.js";

const ICON_BUTTON_CLASS =
  "h-[34px] w-[34px] min-h-11 min-w-11 p-0 has-[>svg]:p-0 [&_svg]:size-3.5 [@media(hover:hover)_and_(pointer:fine)]:min-h-[34px] [@media(hover:hover)_and_(pointer:fine)]:min-w-[34px]";
const FINE_POINTER_FOOTER_H =
  "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-[34px]";
const AI_BUTTON_CLASS =
  "h-[34px] min-h-11 border border-[var(--ai-border)] bg-[var(--ai-fill-light)] text-[var(--ai-text)] hover:bg-[var(--ai-fill-medium)] [@media(hover:hover)_and_(pointer:fine)]:min-h-[34px]";
const LOADING_BUTTON_CLASS =
  "ring-1 ring-[var(--ai)] disabled:opacity-80 [&_svg]:animate-spin";
const SEND_BUTTON_CLASS = `h-[34px] ${FINE_POINTER_FOOTER_H} gap-1.5 px-3.5 text-[13px] font-semibold`;
const COL_GRID =
  "grid grid-cols-1 sm:grid-cols-[4.75rem_minmax(0,1fr)_minmax(0,1fr)]";
const BODY_TEXT = "text-secondary-subtle-foreground";
const CHROME_SELECT_TRIGGER = "h-8 w-auto min-w-[8.5rem] px-2";

type BriefSurface = "card" | "drawer";

interface BriefTypeScale {
  label: string;
  text: string;
  title: string;
  hint: string;
}

/** Set-workspace cards keep their compact scale; the hub drawer uses the shared drawer tokens. */
const TYPE_SCALE: Record<BriefSurface, BriefTypeScale> = {
  card: {
    label:
      "shrink-0 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground",
    text: "text-[12.5px]",
    title: "text-[13px] font-semibold text-foreground",
    hint: "text-[11.5px] text-muted-foreground",
  },
  drawer: {
    label:
      "shrink-0 text-caption font-medium uppercase tracking-label text-muted-foreground",
    text: "text-drawer-body",
    title: "text-drawer-body font-semibold text-foreground",
    hint: "text-caption text-muted-foreground",
  },
};

export interface PersonaBriefExpandedBodyProps {
  brief: PersonaBriefDto;
  canEdit: boolean;
  searchQuery?: string;
  editingKey: string | null;
  savingKey: string | null;
  aiEditKey?: string | null;
  aiBusyKey?: string | null;
  flashKey?: string | null;
  flashSource?: "manual" | "ai";
  onStartEdit: (key: string) => void;
  onStopEdit: (key: string) => void;
  /** The parent clears any open manual edit before opening the cell AI editor. */
  onStartAi?: (key: string) => void;
  onStopAi?: () => void;
  onApplyAi?: (cellKey: string, instruction: string) => void;
  onPersist: (key: string, patch: PersonaBriefContentPatch) => void | Promise<boolean | void>;
  /** Drops a settled failure once a brand draft matches the saved value again. */
  onDiscardSave?: (key: string) => void;
  /** Resolves after any save started by blur, so Share opens the saved brief. */
  beforeShare?: () => Promise<boolean | void>;
  isAiEditing?: boolean;
  onAiEdit?: () => void;
  onSendToVendor?: () => void;
  sentMeta?: SentScriptMeta | null;
  orderHref?: string;
  onOpenOrder?: (event: ReactMouseEvent<HTMLAnchorElement>) => void;
  /** Hub drawer moves share, copy, AI edit, and send into the sheet header. */
  actionsPlacement?: "footer" | "header";
  /** Hub drawer uses the shared drawer type scale. Set-workspace cards stay on `card`. */
  surface?: BriefSurface;
  /** Replaces the editable fields in place, e.g. with a version-history preview. */
  fieldsPreview?: ReactNode;
  /**
   * Show-differences overlay. Keeps this document layout and marks fields that differ
   * from the original version, wording the change against the previous version.
   */
  changes?: PersonaBriefFieldChanges | null;
}

export interface PersonaBriefFieldChanges {
  previous: PersonaBriefDto | undefined;
  original: PersonaBriefDto | undefined;
  blameByKey: ReadonlyMap<string, number>;
  onSelectVersion: (version: number) => void;
}

/** Unfolded brief body shared by the set workspace card and the hub drawer. */
export function PersonaBriefExpandedBody({
  brief,
  canEdit,
  searchQuery = "",
  editingKey,
  savingKey,
  aiEditKey = null,
  aiBusyKey = null,
  flashKey = null,
  flashSource = "manual",
  onStartEdit,
  onStopEdit,
  onStartAi,
  onStopAi,
  onApplyAi,
  onPersist,
  onDiscardSave,
  beforeShare,
  isAiEditing = false,
  onAiEdit,
  onSendToVendor,
  sentMeta = null,
  orderHref,
  onOpenOrder,
  actionsPlacement = "footer",
  surface = "card",
  fieldsPreview,
  changes = null,
}: PersonaBriefExpandedBodyProps) {
  const { t } = useTranslation();
  const drawer = surface === "drawer";
  const scale = TYPE_SCALE[surface];
  const emptyValue = t("personaOpportunities.scripts.emptyValue", "—");
  const optionalEmpty = t(
    "personaOpportunities.scripts.briefs.optionalEmpty",
    "Not set — optional",
  );
  const orderedHooks = [...brief.hooks].sort(
    (a, b) => a.hookIndex - b.hookIndex,
  );
  const hookPackage = brief.hookPackage;
  const isOneHook = hookPackage === 1;
  const duration = brief.duration;
  const brandOverridden = isBrandOverridden(brief.brand);
  const isSaving = savingKey != null;

  const referenceHref = brief.creativeExampleUrl?.trim()
    ? briefReferenceHref(brief.creativeExampleUrl.trim())
    : null;
  const referenceLabel = t(
    "personaOpportunities.scripts.briefs.referenceLink",
    "Top creative for this angle",
  );
  const durationLabel = t(
    "personaOpportunities.scripts.briefs.duration",
    "Duration",
  );
  const hookPackageLabel = t(
    "personaOpportunities.scripts.briefs.hookPackage",
    "Hook package",
  );
  const ageLabel = t(
    "personaOpportunities.scripts.briefs.ageRange",
    "Age range",
  );
  const genderLabel = t("personaOpportunities.scripts.briefs.gender", "Gender");
  const hookPackageOneLabel = t(
    "personaOpportunities.scripts.briefs.hookPackageOne",
    "1 Hook",
  );
  const hookPackageFourLabel = t(
    "personaOpportunities.scripts.briefs.hookPackageFour",
    "4 Hooks",
  );
  const formatClosedListValue = (option: string) =>
    translatePersonaBriefClosedListValue(t, option);
  const selectTriggerClass = cn(CHROME_SELECT_TRIGGER, scale.text);

  function formatChangeDisplay(key: string, raw: string): string {
    if (key === "hookPackage") {
      return raw === "1" ? hookPackageOneLabel : hookPackageFourLabel;
    }
    if (key === "duration" || key === "ageRange" || key === "gender") {
      return formatClosedListValue(raw);
    }
    return raw;
  }

  function renderFieldDiff(
    key: string,
    afterRaw: string,
    textClassName: string = scale.text,
  ) {
    if (!changes?.original) return null;
    const originalRaw = briefChangeRaw(changes.original, key);
    if (originalRaw === afterRaw) return null;
    const previousRaw = changes.previous
      ? briefChangeRaw(changes.previous, key)
      : undefined;
    return (
      <BriefFieldDiff
        before={
          previousRaw == null
            ? undefined
            : formatChangeDisplay(key, previousRaw)
        }
        after={formatChangeDisplay(key, afterRaw)}
        empty={emptyValue}
        blameVersion={changes.blameByKey.get(key) ?? null}
        onSelectVersion={changes.onSelectVersion}
        textClassName={textClassName}
      />
    );
  }

  function renderBriefCell(
    cellKey: string,
    value: string,
    className?: string,
    emptyLabel?: string,
  ) {
    const diff = renderFieldDiff(
      changeKeyForCell(cellKey),
      value,
      cn(CELL_DISPLAY_SIZE_CLASS, "leading-relaxed"),
    );
    if (diff) {
      return <div className={cn(className, "bg-ai-subtle")}>{diff}</div>;
    }
    const isFlashTarget = flashKey === cellKey;
    return (
      <div className={className}>
        <PersonaScriptEditableCell
          value={value}
          canEdit={canEdit}
          showAi={canEdit && !!onApplyAi}
          emptyLabel={emptyLabel}
          rows={3}
          highlightQuery={searchQuery}
          surface={surface}
          isEditing={editingKey === cellKey}
          isSaving={savingKey === cellKey}
          isAiOpen={aiEditKey === cellKey}
          isAiBusy={aiBusyKey === cellKey}
          isFlashing={isFlashTarget && flashSource !== "ai"}
          isFlashingAi={isFlashTarget && flashSource === "ai"}
          onStartEdit={() => {
            onStopAi?.();
            onStartEdit(cellKey);
          }}
          onStopEdit={() => onStopEdit(cellKey)}
          onStartAi={() => onStartAi?.(cellKey)}
          onStopAi={onStopAi}
          onApplyAi={(instruction) => onApplyAi?.(cellKey, instruction)}
          onSave={(next) => {
            const patch = patchForCell(cellKey, next);
            if (!patch) return false;
            return onPersist(cellKey, patch);
          }}
        />
      </div>
    );
  }

  const fields = (
    <>
      <div className="flex flex-wrap items-center gap-x-[22px] gap-y-2 border-b border-border bg-muted px-4 py-[11px]">
        <div className="flex min-w-0 items-center gap-[9px]">
          <span className={scale.label}>
            {t("personaOpportunities.scripts.briefs.reference", "Reference")}
          </span>
          {referenceHref ? (
            <a
              href={referenceHref}
              target="_blank"
              rel="noreferrer"
              className={cn(
                "truncate no-underline hover:underline",
                scale.text,
                BODY_TEXT,
              )}
            >
              {referenceLabel}
            </a>
          ) : (
            <span className={cn(scale.text, "text-muted-foreground")}>
              {emptyValue}
            </span>
          )}
        </div>
        <div className="flex items-center gap-[9px]">
          <span className={scale.label}>{durationLabel}</span>
          {renderFieldDiff("duration", duration) ?? (
            <ClosedListSelect
              ariaLabel={durationLabel}
              value={duration}
              searchQuery={searchQuery}
              options={PERSONA_BRIEF_DURATION_OPTIONS}
              formatOption={formatClosedListValue}
              readOnly={!canEdit}
              disabled={isSaving}
              textClassName={scale.text}
              triggerClassName={selectTriggerClass}
              onValueChange={(next) => {
                if (next === duration) return;
                onPersist("duration", { target: "duration", value: next });
              }}
            />
          )}
        </div>
        <div className="flex items-center gap-[9px]">
          <span className={scale.label}>{hookPackageLabel}</span>
          {renderFieldDiff("hookPackage", String(hookPackage)) ??
            (canEdit ? (
              <Select
                value={String(hookPackage)}
                disabled={isSaving}
                onValueChange={(next) => {
                  const parsed = parseHookPackage(next);
                  if (parsed == null || parsed === hookPackage) return;
                  onPersist("hookPackage", {
                    target: "hookPackage",
                    value: parsed,
                  });
                }}
              >
                <SelectTrigger
                  aria-label={hookPackageLabel}
                  className={selectTriggerClass}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERSONA_BRIEF_HOOK_PACKAGE_OPTIONS.map((pkg) => (
                    <SelectItem key={pkg} value={String(pkg)}>
                      {pkg === 1 ? hookPackageOneLabel : hookPackageFourLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className={cn(scale.text, "text-foreground")}>
                {hookPackage === 1 ? hookPackageOneLabel : hookPackageFourLabel}
              </span>
            ))}
        </div>
      </div>

      <div
        className={cn(
          COL_GRID,
          "hidden border-b border-border bg-background sm:grid",
        )}
      >
        <span className={cn(scale.label, "px-4 py-[9px] pr-3")} />
        <span className={cn(scale.label, "px-3 py-[9px]")}>
          {t(
            "personaOpportunities.scripts.briefs.directionColumn",
            "Creator direction",
          )}
        </span>
        <span className={cn(scale.label, "py-[9px] pr-4 pl-3")}>
          {t(
            "personaOpportunities.scripts.briefs.lineColumn",
            "Line to say on camera",
          )}
        </span>
      </div>

      <section>
        <div className="flex flex-wrap items-baseline gap-2.5 px-4 pt-3 pb-[7px]">
          <h4 className={scale.title}>
            {t("personaOpportunities.scripts.briefs.hooksTitle", "Hooks")}
          </h4>
          <p className={scale.hint}>
            {isOneHook
              ? t(
                  "personaOpportunities.scripts.briefs.hooksHintOne",
                  "One hook is sent — pick which",
                )
              : t(
                  "personaOpportunities.scripts.briefs.hooksHint",
                  "Four options — the production partner films all four",
                )}
          </p>
          {renderFieldDiff(
            "selectedHookIndex",
            String(brief.selectedHookIndex),
          )}
        </div>
        <ul>
          {orderedHooks.map((hook, index) => {
            const isSelected = brief.selectedHookIndex === hook.hookIndex;
            const isDimmed = isOneHook && !isSelected;
            return (
              <li
                key={hook.id}
                className={cn(
                  COL_GRID,
                  "border-b border-border-subtle",
                  hookRowClass(index),
                  isDimmed && "opacity-40",
                )}
              >
                <div className="flex items-center gap-2 py-[11px] pr-3 pl-4">
                  {isOneHook ? (
                    <input
                      type="radio"
                      name={`persona-brief-${brief.id}-selected-hook`}
                      className="size-3.5 shrink-0 accent-primary"
                      checked={isSelected}
                      disabled={!canEdit || isSaving}
                      aria-label={t(
                        "personaOpportunities.scripts.briefs.selectHook",
                        {
                          defaultValue: "Select hook {{index}}",
                          index: hook.hookIndex,
                        },
                      )}
                      onChange={() => {
                        if (isSelected) return;
                        onPersist("selectedHookIndex", {
                          target: "selectedHookIndex",
                          value: hook.hookIndex,
                        });
                      }}
                    />
                  ) : null}
                  <span className="grid size-[22px] place-items-center rounded-full bg-border-subtle text-[11px] tabular-nums text-secondary-subtle-foreground">
                    {hook.hookIndex}
                  </span>
                </div>
                {renderBriefCell(
                  `hook:${hook.hookIndex}:direction`,
                  hook.direction,
                  "px-1 py-1",
                )}
                {renderBriefCell(
                  `hook:${hook.hookIndex}:line`,
                  hook.line,
                  "px-1 py-1 pr-3",
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section>
        <div className="flex flex-wrap items-baseline gap-2.5 px-4 pt-[15px] pb-[7px]">
          <h4 className={scale.title}>
            {t("personaOpportunities.scripts.briefs.conceptTitle", "Concept")}
          </h4>
          <p className={scale.hint}>
            {t(
              "personaOpportunities.scripts.briefs.conceptHint",
              "The body of the video",
            )}
          </p>
        </div>
        <div className={cn(COL_GRID, "border-b border-border-subtle")}>
          <div />
          {renderBriefCell(
            "concept:direction",
            brief.concept.direction,
            "px-1 py-1",
          )}
          {renderBriefCell(
            "concept:line",
            brief.concept.line,
            "px-1 py-1 pr-3",
          )}
        </div>
      </section>

      <section>
        <h4 className={cn("px-4 pt-[15px] pb-[7px]", scale.title)}>
          {t("personaOpportunities.scripts.briefs.ctaTitle", "Call to action")}
        </h4>
        <div className={cn(COL_GRID, "border-b border-border")}>
          <div />
          {renderBriefCell("cta:direction", brief.cta.direction, "px-1 py-1")}
          {renderBriefCell("cta:line", brief.cta.line, "px-1 py-1 pr-3")}
        </div>
      </section>

      <div className="grid grid-cols-1 border-b border-border sm:grid-cols-2">
        <section className="border-b border-border px-4 py-[15px] sm:border-r sm:border-b-0">
          <h4 className={cn("mb-2.5", scale.title)}>
            {t(
              "personaOpportunities.scripts.briefs.profileTitle",
              "Creator profile",
            )}
          </h4>
          <div className={cn("flex flex-col gap-[9px]", scale.text)}>
            <div className="flex items-center gap-3">
              <span className="w-[104px] shrink-0 text-muted-foreground">
                {ageLabel}
              </span>
              {renderFieldDiff("ageRange", brief.creatorProfile.ageRange) ?? (
                <ClosedListSelect
                  ariaLabel={ageLabel}
                  value={brief.creatorProfile.ageRange}
                  searchQuery={searchQuery}
                  options={PERSONA_BRIEF_AGE_RANGE_OPTIONS}
                  formatOption={formatClosedListValue}
                  readOnly={!canEdit}
                  disabled={isSaving}
                  textClassName={scale.text}
                  triggerClassName={selectTriggerClass}
                  onValueChange={(next) => {
                    if (next === brief.creatorProfile.ageRange) return;
                    onPersist("ageRange", { target: "ageRange", value: next });
                  }}
                />
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="w-[104px] shrink-0 text-muted-foreground">
                {genderLabel}
              </span>
              {renderFieldDiff("gender", brief.creatorProfile.gender) ?? (
                <ClosedListSelect
                  ariaLabel={genderLabel}
                  value={brief.creatorProfile.gender}
                  searchQuery={searchQuery}
                  options={PERSONA_BRIEF_GENDER_OPTIONS}
                  formatOption={formatClosedListValue}
                  readOnly={!canEdit}
                  disabled={isSaving}
                  textClassName={scale.text}
                  triggerClassName={selectTriggerClass}
                  onValueChange={(next) => {
                    if (next === brief.creatorProfile.gender) return;
                    if (next !== "Male" && next !== "Female" && next !== "Both")
                      return;
                    onPersist("gender", { target: "gender", value: next });
                  }}
                />
              )}
            </div>
            <div className="flex items-start gap-3">
              <span className="w-[104px] shrink-0 pt-2 text-muted-foreground">
                {t(
                  "personaOpportunities.scripts.briefs.preferences",
                  "Preferences",
                )}
              </span>
              <div className="min-w-0 flex-1">
                {renderBriefCell(
                  "preferences",
                  brief.creatorProfile.preferences ?? "",
                  undefined,
                  optionalEmpty,
                )}
              </div>
            </div>
          </div>
        </section>
        <section className="px-4 py-[15px]">
          <h4 className={cn("mb-[9px]", scale.title)}>
            {t(
              "personaOpportunities.scripts.briefs.notesTitle",
              "Notes for the creator",
            )}
          </h4>
          {renderBriefCell(
            "notes",
            brief.notes ?? "",
            undefined,
            optionalEmpty,
          )}
        </section>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2">
        <section className="border-b border-border px-4 py-[15px] sm:border-r sm:border-b-0">
          <div className="mb-[5px] flex flex-wrap items-center gap-[9px]">
            <h4 className={scale.title}>
              {t("personaOpportunities.scripts.briefs.brandTitle", "Brand")}
            </h4>
            <span className="rounded-sm bg-[var(--slate-chip-bg)] px-[7px] py-px text-[10.5px] text-[var(--slate-chip-text)]">
              {brandOverridden
                ? t(
                    "personaOpportunities.scripts.briefs.overridden",
                    "Overridden",
                  )
                : t(
                    "personaOpportunities.scripts.briefs.inherited",
                    "Inherited",
                  )}
            </span>
            {canEdit && brandOverridden ? (
              <Button
                type="button"
                size="xs"
                variant="secondary"
                disabled={isSaving}
                onClick={() =>
                  onPersist("resetBrand", { target: "resetBrand" })
                }
              >
                {t(
                  "personaOpportunities.scripts.briefs.resetToWorkspace",
                  "Reset to workspace",
                )}
              </Button>
            ) : null}
          </div>
          <p className={cn("mb-2.5 leading-[1.55]", scale.hint)}>
            {t(
              "personaOpportunities.scripts.briefs.inheritedHint",
              "Same on every brief. Override only if this one differs.",
            )}
          </p>
          <div className="flex flex-col gap-0.5">
            {canEdit ? (
              <>
                <BrandOverrideInput
                  id={`persona-brief-${brief.id}-product-link`}
                  label={t(
                    "personaOpportunities.scripts.briefs.productLink",
                    "Product link",
                  )}
                  value={brief.brand.productLink}
                  disabled={isSaving}
                  textClassName={scale.text}
                  onCommit={(next) =>
                    onPersist("productLink", {
                      target: "productLink",
                      value: next,
                    })
                  }
                  onDiscard={() => onDiscardSave?.("productLink")}
                />
                <BrandOverrideInput
                  id={`persona-brief-${brief.id}-font`}
                  label={t("personaOpportunities.scripts.briefs.font", "Font")}
                  value={brief.brand.font}
                  disabled={isSaving}
                  textClassName={scale.text}
                  onCommit={(next) =>
                    onPersist("font", { target: "font", value: next })
                  }
                  onDiscard={() => onDiscardSave?.("font")}
                />
                <BrandOverrideInput
                  id={`persona-brief-${brief.id}-logo`}
                  label={t("personaOpportunities.scripts.briefs.logo", "Logo")}
                  value={brief.brand.logo}
                  disabled={isSaving}
                  textClassName={scale.text}
                  onCommit={(next) =>
                    onPersist("logo", { target: "logo", value: next })
                  }
                  onDiscard={() => onDiscardSave?.("logo")}
                />
                <BrandOverrideInput
                  id={`persona-brief-${brief.id}-disclaimer`}
                  label={t(
                    "personaOpportunities.scripts.briefs.disclaimer",
                    "Disclaimer",
                  )}
                  value={brief.brand.disclaimer}
                  disabled={isSaving}
                  textClassName={scale.text}
                  onCommit={(next) =>
                    onPersist("disclaimer", {
                      target: "disclaimer",
                      value: next,
                    })
                  }
                  onDiscard={() => onDiscardSave?.("disclaimer")}
                />
              </>
            ) : (
              <>
                {(
                  [
                    [
                      "productLink",
                      t(
                        "personaOpportunities.scripts.briefs.productLink",
                        "Product link",
                      ),
                      brief.brand.productLink,
                    ],
                    [
                      "font",
                      t("personaOpportunities.scripts.briefs.font", "Font"),
                      brief.brand.font,
                    ],
                    [
                      "logo",
                      t("personaOpportunities.scripts.briefs.logo", "Logo"),
                      brief.brand.logo,
                    ],
                    [
                      "disclaimer",
                      t(
                        "personaOpportunities.scripts.briefs.disclaimer",
                        "Disclaimer",
                      ),
                      brief.brand.disclaimer,
                    ],
                  ] as const
                ).map(([key, label, value]) => {
                  const diff = renderFieldDiff(key, value ?? "");
                  if (diff) {
                    return (
                      <div key={key} className="bg-ai-subtle px-1.5 py-1">
                        <div className="flex items-start gap-3">
                          <span
                            className={cn(
                              "w-[104px] shrink-0 text-muted-foreground",
                              scale.text,
                            )}
                          >
                            {label}
                          </span>
                          <div className="min-w-0 flex-1">{diff}</div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <BrandFieldRow
                      key={key}
                      label={label}
                      value={value?.trim() || emptyValue}
                      textClassName={scale.text}
                    />
                  );
                })}
              </>
            )}
          </div>
        </section>
        <section className="bg-muted px-4 py-[15px]">
          <div className="mb-[5px] flex flex-wrap items-center gap-[9px]">
            <h4 className={scale.title}>
              {t(
                "personaOpportunities.scripts.briefs.orderTitle",
                "Order details",
              )}
            </h4>
            <span className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-px text-[10.5px] text-muted-foreground">
              <Icon name="lock" size="xs" color="inherit" aria-hidden />
              {t(
                "personaOpportunities.scripts.briefs.chosenAtSend",
                "Chosen at send",
              )}
            </span>
          </div>
          <p className={cn("mb-2.5 leading-[1.55]", scale.hint)}>
            {t(
              "personaOpportunities.scripts.briefs.orderHint",
              "These depend on budget and campaign, not the creative — you set them when you send. The same brief can go out more than once.",
            )}
          </p>
          <div
            className={cn(
              "grid grid-cols-[118px_minmax(0,1fr)] items-baseline gap-x-3.5 gap-y-[7px]",
              scale.text,
            )}
          >
            <span className="text-muted-foreground">
              {t(
                "personaOpportunities.scripts.briefs.aspectRatio",
                "Aspect ratio",
              )}
            </span>
            <span className={BODY_TEXT}>{emptyValue}</span>
            <span className="text-muted-foreground">
              {t(
                "personaOpportunities.scripts.briefs.videoCount",
                "Number of videos",
              )}
            </span>
            <span className="text-muted-foreground">{emptyValue}</span>
            <span className="text-muted-foreground">
              {t(
                "personaOpportunities.scripts.briefs.productShipped",
                "Product shipped",
              )}
            </span>
            <span className="text-muted-foreground">{emptyValue}</span>
          </div>
        </section>
      </div>
    </>
  );

  const aiEditingStatus = isAiEditing ? (
    <p
      role="status"
      className="flex items-center gap-2 rounded-md bg-[var(--ai-fill-light)] px-3 py-2 text-xs text-[var(--ai-text)]"
    >
      <Icon name="loader" color="inherit" size="sm" className="animate-spin" />
      {t(
        "personaOpportunities.scripts.briefs.aiEditor.editing",
        "AI editing this brief...",
      )}
    </p>
  ) : null;

  const actions =
    actionsPlacement === "footer" ? (
      <BriefActionsFooter
        brief={brief}
        canEdit={canEdit}
        isAiEditing={isAiEditing}
        aiEditingStatus={aiEditingStatus}
        onAiEdit={onAiEdit}
        onSendToVendor={onSendToVendor}
        sentMeta={sentMeta}
        orderHref={orderHref}
        onOpenOrder={onOpenOrder}
        beforeShare={beforeShare}
      />
    ) : (
      aiEditingStatus
    );

  if (drawer) {
    return (
      <div className="mt-4 space-y-4 text-foreground">
        {fieldsPreview ?? (
          <div className="overflow-hidden rounded-sm border border-border bg-background">
            {fields}
          </div>
        )}
        {actions}
      </div>
    );
  }

  return (
    <div className="border-t border-border">
      {fieldsPreview ?? fields}
      {actions}
    </div>
  );
}

function BriefActionsFooter({
  brief,
  canEdit,
  isAiEditing,
  aiEditingStatus,
  onAiEdit,
  onSendToVendor,
  sentMeta,
  orderHref,
  onOpenOrder,
  beforeShare,
}: {
  brief: PersonaBriefDto;
  canEdit: boolean;
  isAiEditing: boolean;
  aiEditingStatus: ReactNode;
  onAiEdit?: () => void;
  onSendToVendor?: () => void;
  sentMeta: SentScriptMeta | null;
  orderHref?: string;
  onOpenOrder?: (event: ReactMouseEvent<HTMLAnchorElement>) => void;
  beforeShare?: () => Promise<boolean | void>;
}) {
  const { t } = useTranslation();
  const { copied, isSharing, share, copy } = usePersonaBriefClipboard(brief, { beforeShare });

  return (
    <footer className="flex flex-col gap-2 border-t border-border bg-muted px-4 py-3">
      {aiEditingStatus}
      <div className="flex flex-wrap items-center gap-2.5">
        <Button
          type="button"
          variant="secondary"
          className={cn(ICON_BUTTON_CLASS, isSharing && "disabled:opacity-100")}
          iconStart={isSharing ? undefined : "share"}
          onClick={() => void share()}
          disabled={isSharing}
          aria-busy={isSharing}
          aria-label={t("personaOpportunities.scripts.actions.share", "Share")}
        >
          {isSharing ? (
            <Icon
              name="loader"
              color="inherit"
              size="sm"
              className="animate-spin motion-reduce:animate-none"
            />
          ) : null}
        </Button>
        <Button
          type="button"
          variant="secondary"
          className={ICON_BUTTON_CLASS}
          iconStart={copied ? "check" : "copy"}
          onClick={() => void copy()}
          aria-label={
            copied
              ? t("personaOpportunities.scripts.actions.copied", "Copied!")
              : t("personaOpportunities.scripts.briefs.copy", "Copy brief")
          }
        />
        {canEdit && onAiEdit ? (
          <Button
            type="button"
            variant="ai"
            iconStart={isAiEditing ? "loader" : "sparkles"}
            disabled={isAiEditing}
            aria-busy={isAiEditing}
            onClick={onAiEdit}
            className={
              isAiEditing
                ? `${AI_BUTTON_CLASS} ${LOADING_BUTTON_CLASS}`
                : AI_BUTTON_CLASS
            }
          >
            {t("personaOpportunities.scripts.actions.aiEdit", "AI Edit")}
          </Button>
        ) : null}
        <span className="min-w-2 flex-1" />
        {sentMeta ? (
          <ScriptSentToVendorCard
            sentMeta={sentMeta}
            orderHref={orderHref}
            onOpenOrder={onOpenOrder}
          />
        ) : (
          <Button
            type="button"
            className={SEND_BUTTON_CLASS}
            onClick={() => onSendToVendor?.()}
            disabled={!onSendToVendor}
          >
            <PaperPlaneIcon />
            {t(
              "personaOpportunities.scripts.briefs.send",
              "Send to production",
            )}
          </Button>
        )}
      </div>
    </footer>
  );
}

function changeKeyForCell(cellKey: string): string {
  const hook = /^hook:(\d+):(direction|line)$/.exec(cellKey);
  if (hook) return `hook-${hook[1]}-${hook[2]}`;
  if (cellKey === "concept:direction") return "conceptDirection";
  if (cellKey === "concept:line") return "conceptLine";
  if (cellKey === "cta:direction") return "ctaDirection";
  if (cellKey === "cta:line") return "ctaLine";
  return cellKey;
}

function briefChangeRaw(brief: PersonaBriefDto, key: string): string {
  const hook = /^hook-(\d+)-(direction|line)$/.exec(key);
  if (hook) {
    const row = brief.hooks.find((item) => item.hookIndex === Number(hook[1]));
    return row?.[hook[2] as "direction" | "line"] ?? "";
  }
  switch (key) {
    case "duration":
      return brief.duration;
    case "hookPackage":
      return String(brief.hookPackage);
    case "selectedHookIndex":
      return String(brief.selectedHookIndex);
    case "ageRange":
      return brief.creatorProfile.ageRange;
    case "gender":
      return brief.creatorProfile.gender;
    case "conceptDirection":
      return brief.concept.direction;
    case "conceptLine":
      return brief.concept.line;
    case "ctaDirection":
      return brief.cta.direction;
    case "ctaLine":
      return brief.cta.line;
    case "preferences":
      return brief.creatorProfile.preferences ?? "";
    case "notes":
      return brief.notes ?? "";
    case "productLink":
      return brief.brand.productLink ?? "";
    case "font":
      return brief.brand.font ?? "";
    case "logo":
      return brief.brand.logo ?? "";
    case "disclaimer":
      return brief.brand.disclaimer ?? "";
    default:
      return "";
  }
}

function BriefFieldDiff({
  before,
  after,
  empty,
  blameVersion,
  onSelectVersion,
  textClassName,
}: {
  before: string | undefined;
  after: string;
  empty: string;
  blameVersion: number | null;
  onSelectVersion: (version: number) => void;
  textClassName: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="min-w-0">
      {blameVersion != null ? (
        <button
          type="button"
          onClick={() => onSelectVersion(blameVersion)}
          className="mb-1 block shrink-0 cursor-pointer text-caption font-medium tabular-nums text-ai-text transition-colors duration-150 ease-out hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          aria-label={t(
            "personaOpportunities.scripts.history.changedInVersion",
            {
              defaultValue: "Changed in v{{version}}",
              version: blameVersion,
            },
          )}
        >
          {t("personaOpportunities.scripts.history.versionShort", {
            defaultValue: "v{{version}}",
            version: blameVersion,
          })}
        </button>
      ) : null}
      <p className={cn(textClassName, "whitespace-pre-wrap text-foreground")}>
        <PersonaScriptFieldDiff before={before} after={after} empty={empty} />
      </p>
    </div>
  );
}

function briefReferenceHref(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

function isBrandOverridden(brand: PersonaBriefDto["brand"]): boolean {
  return (
    brand.productLink != null ||
    brand.font != null ||
    brand.logo != null ||
    brand.disclaimer != null
  );
}

function parseHookPackage(value: string): PersonaBriefHookPackage | null {
  if (value === "1") return 1;
  if (value === "4") return 4;
  return null;
}

function hookRowClass(index: number): string {
  return index % 2 === 1 ? "bg-muted" : "bg-background";
}

function PaperPlaneIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-3.5 shrink-0"
      data-icon="send"
    >
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  );
}

function ClosedListSelect({
  ariaLabel,
  value,
  searchQuery,
  options,
  formatOption,
  readOnly,
  disabled,
  textClassName,
  triggerClassName,
  onValueChange,
}: {
  ariaLabel: string;
  value: string;
  searchQuery: string;
  options: readonly string[];
  formatOption: (value: string) => string;
  readOnly?: boolean;
  disabled?: boolean;
  textClassName: string;
  triggerClassName: string;
  onValueChange: (value: string) => void;
}) {
  const display = formatOption(value);

  if (readOnly) {
    return (
      <span className={cn(textClassName, "text-foreground")}>
        <HighlightText text={display} query={searchQuery} />
      </span>
    );
  }

  const items = withCurrentOption(options, value);

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger aria-label={ariaLabel} className={triggerClassName}>
        <SelectValue>
          <HighlightText text={display} query={searchQuery} />
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {items.map((option) => (
          <SelectItem key={option} value={option}>
            {formatOption(option)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function BrandFieldRow({
  label,
  value,
  textClassName,
}: {
  label: string;
  value: string;
  textClassName: string;
}) {
  return (
    <div className="flex items-start gap-3 px-1.5 py-1">
      <span
        className={cn(
          "w-[104px] shrink-0 text-muted-foreground",
          textClassName,
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 leading-[1.55]",
          textClassName,
          BODY_TEXT,
        )}
      >
        {value}
      </span>
    </div>
  );
}

function BrandOverrideInput({
  id,
  label,
  value,
  disabled,
  textClassName,
  onCommit,
  onDiscard,
}: {
  id: string;
  label: string;
  value: string | null;
  disabled?: boolean;
  textClassName: string;
  onCommit: (next: string | null) => void;
  onDiscard?: () => void;
}) {
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  function commit() {
    const next = draft.trim() === "" ? null : draft.trim();
    const current = value == null || value.trim() === "" ? null : value.trim();
    // The draft matches the server again, so a failed save of this field
    // should not keep blocking Share.
    if (next === current) {
      onDiscard?.();
      return;
    }
    onCommit(next);
  }

  return (
    <div className="flex items-start gap-3 px-1.5 py-1">
      <label
        htmlFor={id}
        className={cn(
          "w-[104px] shrink-0 pt-1.5 text-muted-foreground",
          textClassName,
        )}
      >
        {label}
      </label>
      <Input
        id={id}
        value={draft}
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            (event.target as HTMLInputElement).blur();
          }
        }}
        className={cn("h-8 flex-1", textClassName)}
      />
    </div>
  );
}

function patchForCell(
  cellKey: string,
  value: string,
): PersonaBriefContentPatch | null {
  if (cellKey === "notes") return { target: "notes", value };
  if (cellKey === "preferences") return { target: "preferences", value };
  if (cellKey === "concept:direction")
    return { target: "concept", field: "direction", value };
  if (cellKey === "concept:line")
    return { target: "concept", field: "line", value };
  if (cellKey === "cta:direction")
    return { target: "cta", field: "direction", value };
  if (cellKey === "cta:line") return { target: "cta", field: "line", value };

  const hookMatch = /^hook:(\d+):(direction|line)$/.exec(cellKey);
  if (!hookMatch) return null;
  return {
    target: "hook",
    hookIndex: Number(hookMatch[1]),
    field: hookMatch[2] as "direction" | "line",
    value,
  };
}
